import React, { useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { AdminScan, AdminScanHealth } from '../services/api';
import { SegmentedToggle } from './SegmentedToggle';

/** Why a line wasn't priced, in plain words (the scanner's line statuses; "ok" is priced). */
export const SCAN_STATUS: Record<string, { label: string; note: string }> = {
  unverified: { label: 'One read only', note: 'only one of the two AI reads saw the line' },
  disagree: { label: 'Reads differ', note: 'the two reads read different prices' },
  unclear: { label: 'Market unclear', note: 'no market printed and the prices fit two (the user can pick)' },
  suspect: { label: 'Failed checks', note: "the board's prices didn't add up, or a driver's ran the wrong way" },
  unmatched: { label: 'Driver not in field', note: "a name that isn't in the simulated field (often expected)" },
  unpriced: { label: 'Market not priced', note: "groups and other markets we don't price yet" },
};
/** Statuses that point at the scanner (the others are expected: drivers not racing, markets we don't price). */
const SCANNER_MISSES = ['unverified', 'disagree', 'unclear', 'suspect'];
/** A scan counts as a problem when the scanner itself lost this share of its lines, or it priced nothing. */
const PROBLEM_SHARE = 0.25;

const pct = (v: number) => `${Math.round(v * 100)}%`;
const missed = (s: AdminScan) => SCANNER_MISSES.reduce((t, k) => t + (s.statuses[k] ?? 0), 0);
const isProblem = (s: AdminScan) => s.nPriced === 0 || missed(s) / s.nLines >= PROBLEM_SHARE;
const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
/** Monday of the week (local time), as "Oct 27". */
const weekOf = (iso: string) => {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const Tile: React.FC<{ label: string; value: string; note?: string; tone?: string }> = ({
  label,
  value,
  note,
  tone,
}) => (
  <Paper sx={{ flex: '1 1 160px', minWidth: 0, p: 2 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: tone }}>
      {value}
    </Typography>
    {note && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{note}</Typography>}
  </Paper>
);

const Head: React.FC<{ cols: string[]; leftCols?: number }> = ({ cols, leftCols = 1 }) => (
  <TableHead>
    <TableRow>
      {cols.map((h, i) => (
        <TableCell
          key={h + i}
          align={i < leftCols ? 'left' : 'right'}
          sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          {h.toUpperCase()}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

/**
 * The admin page's Board scanner tab: how well the scanner read the boards users sent (from the saved scans), so a
 * bad week, a bad board layout or a user who's struggling shows up without anyone having to report it.
 */
export const AdminBoardScanner: React.FC<{
  health: AdminScanHealth;
  emails: Record<string, string>;
  /** The page's Users-tab user for a hash, opened when a scan's user is clicked. */
  onUser?: (hash: string) => void;
}> = ({ health, emails, onUser }) => {
  const theme = useTheme();
  const [only, setOnly] = useState<'all' | 'problems'>('all');
  const scans = health.scans;
  const t = useMemo(() => {
    const lines = scans.reduce((a, s) => a + s.nLines, 0);
    const priced = scans.reduce((a, s) => a + s.nPriced, 0);
    const statuses: Record<string, number> = {};
    scans.forEach((s) => Object.entries(s.statuses).forEach(([k, n]) => (statuses[k] = (statuses[k] ?? 0) + n)));
    const users = new Set(scans.map((s) => s.userHash ?? '')).size;
    const problems = scans.filter(isProblem).length;
    const weeks = new Map<number, { start: Date; scans: number; lines: number; priced: number; missed: number }>();
    scans.forEach((s) => {
      const w = weekOf(s.scannedAt);
      const e = weeks.get(w.getTime()) ?? { start: w, scans: 0, lines: 0, priced: 0, missed: 0 };
      e.scans++;
      e.lines += s.nLines;
      e.priced += s.nPriced;
      e.missed += missed(s);
      weeks.set(w.getTime(), e);
    });
    return {
      lines,
      priced,
      statuses,
      users,
      problems,
      missed: SCANNER_MISSES.reduce((a, k) => a + (statuses[k] ?? 0), 0),
      weeks: Array.from(weeks.values()).sort((a, b) => b.start.getTime() - a.start.getTime()),
    };
  }, [scans]);
  const good = theme.palette.success.main;
  const bad = theme.palette.error.main;
  const shareColor = (v: number) => (v >= 0.9 ? good : v >= 0.7 ? theme.palette.warning.main : bad);
  const shown = only === 'problems' ? scans.filter(isProblem) : scans;
  const who = (s: AdminScan) => (s.userHash ? emails[s.userHash] || `user ${s.userHash.slice(0, 6)}` : '–');

  if (scans.length === 0) return <Typography color="text.secondary">No board scans in this range.</Typography>;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        <Tile label="SCANS" value={String(scans.length)} note={`${t.users} user${t.users === 1 ? '' : 's'}`} />
        <Tile
          label="LINES READ"
          value={t.lines.toLocaleString()}
          note={`${(t.lines / scans.length).toFixed(0)} a scan`}
        />
        <Tile
          label="PRICED"
          value={t.lines ? pct(t.priced / t.lines) : '–'}
          note={`${t.priced.toLocaleString()} lines`}
          tone={t.lines ? shareColor(t.priced / t.lines) : undefined}
        />
        <Tile
          label="LOST BY THE SCANNER"
          value={t.lines ? pct(t.missed / t.lines) : '–'}
          note="one read only, reads differ, unclear or failed checks"
          tone={t.lines && t.missed / t.lines > 0.1 ? bad : undefined}
        />
        <Tile
          label="PROBLEM SCANS"
          value={String(t.problems)}
          note={`lost ${pct(PROBLEM_SHARE)}+ of their lines, or priced nothing`}
          tone={t.problems ? bad : undefined}
        />
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Why lines weren't priced</Typography>
        {/* One bar for every line read: priced, then each reason, so the shares read at a glance. */}
        <Stack direction="row" sx={{ height: 14, borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
          {[
            ['ok', good],
            ...Object.keys(SCAN_STATUS).map((k) => [k, SCANNER_MISSES.includes(k) ? bad : theme.palette.text.disabled]),
          ].map(
            ([k, c], i) =>
              (t.statuses[k] ?? 0) > 0 && (
                <Tooltip key={k} title={`${k === 'ok' ? 'Priced' : SCAN_STATUS[k].label}: ${t.statuses[k]}`}>
                  <Box
                    sx={{
                      width: `${((t.statuses[k] ?? 0) / t.lines) * 100}%`,
                      bgcolor: alpha(c, k === 'ok' ? 0.85 : 0.35 + 0.1 * (i % 3)),
                    }}
                  />
                </Tooltip>
              ),
          )}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ rowGap: 1 }}>
          {Object.entries(SCAN_STATUS).map(([k, s]) => (
            <Tooltip key={k} title={s.note}>
              <Chip
                size="small"
                variant="outlined"
                label={`${s.label} · ${t.statuses[k] ?? 0}${t.lines ? ` (${pct((t.statuses[k] ?? 0) / t.lines)})` : ''}`}
                sx={{
                  borderColor: SCANNER_MISSES.includes(k) && (t.statuses[k] ?? 0) > 0 ? alpha(bad, 0.6) : undefined,
                }}
              />
            </Tooltip>
          ))}
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
          Red: lines the scanner itself lost. Gray: expected (drivers not in the field, markets not priced yet).
        </Typography>
      </Paper>

      <Paper sx={{ overflowX: 'auto' }}>
        <Typography sx={{ fontWeight: 700, px: 2, pt: 2 }}>By week</Typography>
        <Table size="small">
          <Head cols={['Week of', 'Scans', 'Lines', 'Priced', 'Lost by the scanner']} />
          <TableBody>
            {t.weeks.map((w) => (
              <TableRow key={w.start.getTime()}>
                <TableCell>{w.start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</TableCell>
                <TableCell align="right">{w.scans}</TableCell>
                <TableCell align="right">{w.lines}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 700, color: w.lines ? shareColor(w.priced / w.lines) : undefined }}
                >
                  {w.lines ? pct(w.priced / w.lines) : '–'}
                </TableCell>
                <TableCell align="right">{w.lines ? pct(w.missed / w.lines) : '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ overflowX: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2, pt: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>Scans</Typography>
          <SegmentedToggle
            size="small"
            aria-label="Which scans"
            value={only}
            onChange={setOnly}
            options={[
              { value: 'all', label: `All ${scans.length}` },
              { value: 'problems', label: `Problems ${t.problems}` },
            ]}
          />
        </Stack>
        <Table size="small">
          <Head cols={['When', 'User', 'Track', 'Book', 'Shots', 'Lines', 'Priced', 'Not priced']} leftCols={4} />
          <TableBody>
            {shown.map((s) => {
              const share = s.nLines ? s.nPriced / s.nLines : 0;
              return (
                <TableRow key={s.scanId} sx={{ bgcolor: isProblem(s) ? alpha(bad, 0.06) : undefined }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={`Scan ${s.scanId}${s.geminiModel ? ` · ${s.geminiModel}` : ''}`}>
                      <span>{when(s.scannedAt)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    onClick={s.userHash && onUser ? () => onUser(s.userHash!) : undefined}
                    sx={{ cursor: s.userHash && onUser ? 'pointer' : undefined, fontWeight: 600 }}
                  >
                    {who(s)}
                  </TableCell>
                  <TableCell>{s.site || '–'}</TableCell>
                  <TableCell>{s.book || '–'}</TableCell>
                  <TableCell align="right">{s.nImages}</TableCell>
                  <TableCell align="right">{s.nLines}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: s.nLines ? shareColor(share) : bad }}>
                    {s.nLines ? `${s.nPriced} · ${pct(share)}` : 'nothing read'}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
                      {Object.entries(s.statuses)
                        .filter(([k]) => k !== 'ok')
                        .map(([k, n]) => (
                          <Chip
                            key={k}
                            size="small"
                            label={`${SCAN_STATUS[k]?.label ?? k} ${n}`}
                            sx={{
                              height: 20,
                              fontSize: 11,
                              bgcolor: SCANNER_MISSES.includes(k) ? alpha(bad, 0.15) : undefined,
                            }}
                          />
                        ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};
